import userProfilePicture from 'assets/images/users/user-dummy-img.jpg';
import { genders } from 'constants/campaign';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchDiscoveredCreatorThunk } from 'slices/creatorDiscovery/thunk';
import { toggleApplicationView, toggleManageCollectionModal } from 'slices/ui/reducer';
import { AppDispatch, RootState } from 'store';
import { DiscoveredCreator, DiscoveryView } from 'types/creator-discovery';
import { SocialAccount } from 'types/influencer';
import { formatNumber } from 'utils/helpers';

interface DiscoveredCreatorCardProps {
    creator: DiscoveredCreator;
    isSelected?: boolean;
    onToggleSelect?: (creatorId: string) => void;
    onBookmarkChange?: (creatorId: string, isBookmarked: boolean) => void;
    view?: DiscoveryView;
}

const toSocialAccount = (creator: DiscoveredCreator, isBookmarked: boolean): SocialAccount =>
    ({
        uuid: creator.uuid,
        name: creator.name,
        handle: creator.handle,
        platform: creator.platform,
        followers_count: creator.followers_count,
        profile_picture_url: creator.profile_picture_url ?? '',
        engagement_score: creator.engagement_score ?? '',
        is_bookmarked: isBookmarked,
        bookmark_collections: creator.bookmark_collections,
    }) as SocialAccount;

const DiscoveredCreatorCard = ({
    creator,
    isSelected = false,
    onToggleSelect,
    onBookmarkChange,
    view = 'list',
}: DiscoveredCreatorCardProps) => {
    const gender = creator.ig?.gender;
    const ageBucket = creator.ig?.age_bucket;
    const engagementScore = creator.engagement_score ?? 'NA';
    const isBookmarked = !!creator.is_bookmarked;
    const dispatch: AppDispatch = useDispatch();
    const { discoveryId } = useParams();
    const bestCreative = useSelector((state: RootState) => state.creatorDiscovery.creator[discoveryId!]?.[creator.uuid]?.creator.social_post);

    const handleBookmark = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        dispatch(
            toggleManageCollectionModal({
                post: null,
                creator: toSocialAccount(creator, isBookmarked),
                itemType: 'account',
                addMode: true,
            })
        );
        if (!isBookmarked) {
            onBookmarkChange?.(creator.uuid, true);
        }
    };

    return (
        <article
            className={`discovered-creator-row${view === 'card' ? ' discovered-creator-row--card' : ''}${isSelected ? ' discovered-creator-row--selected' : ''}`}
            onClick={() => {
                if (discoveryId) {
                    dispatch(fetchDiscoveredCreatorThunk({
                        discoveryId,
                        socialAccountId: creator.uuid,
                    }));
                }
                dispatch(toggleApplicationView({
                    socialAccountId: creator.uuid,
                    showPermalink: false,
                    bestCreative,
                }));
            }}
        >
            <div
                className="discovered-creator-row__select"
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.(creator.uuid);
                }}
            >
                <input
                    type="checkbox"
                    className="form-check-input discovered-creator-row__checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect?.(creator.uuid)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${creator.name}`}
                />
            </div>
            <div className="discovered-creator-row__profile">
                <div className="discovered-creator-row__avatar-wrap">
                    <img
                        src={creator.profile_picture_url?.split('=')[0] ?? userProfilePicture}
                        referrerPolicy="no-referrer"
                        className="discovered-creator-row__avatar"
                        alt={creator.name}
                    />
                    {creator.platform === 'ig' && (
                        <span className="discovered-creator-row__platform-badge">
                            <i className="ri-instagram-line" />
                        </span>
                    )}
                    {creator.platform === 'yt' && (
                        <span className="discovered-creator-row__platform-badge discovered-creator-row__platform-badge--yt">
                            <i className="ri-youtube-fill" />
                        </span>
                    )}
                </div>
                <div className="discovered-creator-row__info">
                    <div className="discovered-creator-row__name-row">
                        <span className="discovered-creator-row__name" title={creator.name}>{creator.name}</span>
                    </div>
                    {(gender || ageBucket) && (
                        <div className="discovered-creator-row__tags">
                            {gender && (
                                <span className="discovered-creator-row__tag">
                                    {genders[gender as keyof typeof genders] ?? gender}
                                </span>
                            )}
                            {ageBucket && <span className="discovered-creator-row__tag">{ageBucket}</span>}
                        </div>
                    )}
                </div>
            </div>

            <div className="discovered-creator-row__metrics">
                <div className="discovered-creator-row__metric">
                    <span className="discovered-creator-row__metric-label">
                        Engg. Score
                    </span>
                    <span className="discovered-creator-row__metric-value">{engagementScore}</span>
                </div>
                <div className="discovered-creator-row__metric">
                    <span className="discovered-creator-row__metric-label">
                        Avg Views
                    </span>
                    <span className="discovered-creator-row__metric-value">
                        {creator.average_views ? formatNumber(creator.average_views) : 'NA'}
                    </span>
                </div>
                <div className="discovered-creator-row__metric">
                    <span className="discovered-creator-row__metric-label">Followers</span>
                    <span className="discovered-creator-row__metric-value">
                        {formatNumber(creator.followers_count)}
                    </span>
                </div>
            </div>

            <div className="discovered-creator-row__actions">
                <button
                    type="button"
                    className={`discovered-creator-row__action-btn${isBookmarked ? ' discovered-creator-row__action-btn--bookmarked' : ''}`}
                    aria-label={isBookmarked ? `Manage collections for ${creator.name}` : `Bookmark ${creator.name}`}
                    onClick={handleBookmark}
                >
                    <i className={isBookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                </button>
            </div>
        </article>
    );
};

export default DiscoveredCreatorCard;
